// Meta Ads Manager hooks (ads/*, /marketing) — the on-demand, user-initiated ads
// reads/controls, over the generated client via the tenant-keyed wrappers. Keys are
// ['band', bandId, 'ads-campaigns', account] and ['band', bandId, 'ads-insights', account]. Unlike the composed
// marketing-overview read, these DO reach a vendor (ads reads are core-api, not the background sync), so
// they are gated behind an explicit "open the Ads Manager" toggle (never fired on a plain /marketing load). Every
// response carries an `apiStatus` strip so a missing ads_management grant is an explained CTA, never a silent zero.
import { useQueryClient } from "@tanstack/react-query";
import type { components } from "@cntrl/api-client";
import { useApp } from "../appContext.js";
import { useBandContext } from "../bandContext.js";
import { useBandQuery, useBandMutation } from "./useBandQuery.js";
import { ApiError, errorMessage } from "./apiError.js";
import { pushToast } from "../toasts.js";

export type AdsCampaignsView = components["schemas"]["AdsCampaignsView"];
export type AdsInsightsView = components["schemas"]["AdsInsightsView"];
export type AdCampaignView = components["schemas"]["AdCampaignView"];
export type AdsApiStatus = components["schemas"]["AdsApiStatus"];
export type AdAccountOption = components["schemas"]["AdAccountOption"];
export type AudienceMarket = components["schemas"]["AudienceMarket"];
export type DmaInsightRow = components["schemas"]["DmaInsightRow"];
export type AdsKpiWindow = components["schemas"]["AdsKpiWindow"];
export type CampaignControlRequest = components["schemas"]["CampaignControlRequest"];
export type AdCampaignControlResult = components["schemas"]["AdCampaignControlResult"];
export type AdCampaignCounts = components["schemas"]["AdCampaignCounts"];

/** GET the campaigns table (releases:read). Pure read — the picker + campaigns + fan markets + status strip.
 * `enabled` gates the vendor read behind the Ads-Manager expand. `adAccountId` switches the previewed account. */
export function useAdsCampaigns(adAccountId: string | null, enabled: boolean) {
  const { api } = useApp();
  return useBandQuery<AdsCampaignsView>(
    ["ads-campaigns", adAccountId ?? ""],
    async (bandId) => {
      const { data, error, response } = await api.GET("/api/v1/bands/{bandId}/ads/campaigns", {
        params: { path: { bandId }, query: adAccountId ? { adAccountId } : {} },
      });
      if (!response.ok || !data?.data) throw new ApiError(response.status, error);
      return data.data;
    },
    { enabled, staleTime: 30_000 },
  );
}

/** GET the account-level insights (releases:read) — the KPI strip (vs prev) + DMA. This is the on-demand REFRESH:
 * server-side it lands the KPI snapshot (→ overview tiles) + syncs tagged-campaign spend to the release budget. */
export function useAdsInsights(adAccountId: string | null, enabled: boolean) {
  const { api } = useApp();
  return useBandQuery<AdsInsightsView>(
    ["ads-insights", adAccountId ?? ""],
    async (bandId) => {
      const { data, error, response } = await api.GET("/api/v1/bands/{bandId}/ads/insights", {
        params: { path: { bandId }, query: adAccountId ? { adAccountId } : {} },
      });
      if (!response.ok || !data?.data) throw new ApiError(response.status, error);
      return data.data;
    },
    { enabled, staleTime: 30_000 },
  );
}

export type ControlVariables = { id: string } & CampaignControlRequest;

/** POST an inline campaign control (pause/resume/budget/close). On success invalidates the campaigns table +
 * insights so the change round-trips within the session.*/
export function useControlCampaign() {
  const { api } = useApp();
  const { bandId } = useBandContext();
  const qc = useQueryClient();
  return useBandMutation<AdCampaignControlResult, ControlVariables>(
    async (band, { id, action, dailyBudgetCents }) => {
      const { data, error, response } = await api.POST("/api/v1/bands/{bandId}/ads/campaigns/{id}/control", {
        params: { path: { bandId: band, id } },
        body: { action, dailyBudgetCents: dailyBudgetCents ?? null },
      });
      if (!response.ok || !data?.data) throw new ApiError(response.status, error);
      return data.data;
    },
    {
      onSuccess: () => {
        void qc.invalidateQueries({ queryKey: ["band", bandId, "ads-campaigns"] });
        void qc.invalidateQueries({ queryKey: ["band", bandId, "ads-insights"] });
      },
      onError: (err) => pushToast(errorMessage(err, "Couldn't update the campaign")),
    },
  );
}

/**
 * PUT a campaign's release tag, or remove it (`releaseId: null`) — the campaigns table's Release column.
 *
 * `releases:write` only, NOT the control route's money pair: the tag names an association and carries no figure.
 *
 * OPTIMISTIC (ART-275). The tag is a row in our own database and changes nothing Meta knows, so the campaigns
 * table is patched in place the moment a release is picked — and NOT re-fetched: that read is a live Meta walk
 * (campaigns + batched insights) taking several seconds, which is what made a pick look like it did nothing. A
 * failed save restores the previous tag, so the column still ends on what was SAVED (legacy's rule).
 */
type TagVars = { id: string; releaseId: string | null };
type TagContext = { previous: [readonly unknown[], AdsCampaignsView | undefined][] };

export function useTagCampaign() {
  const { api } = useApp();
  const { bandId } = useBandContext();
  const qc = useQueryClient();
  const key = ["band", bandId, "ads-campaigns"];
  return useBandMutation<void, TagVars, TagContext>(
    async (band, { id, releaseId }) => {
      const { error, response } = await api.PUT("/api/v1/bands/{bandId}/ads/campaigns/{id}/tag", {
        params: { path: { bandId: band, id } },
        body: { releaseId },
      });
      if (!response.ok) throw new ApiError(response.status, error);
    },
    {
      onMutate: async ({ id, releaseId }) => {
        // Stop an in-flight table read landing over the patch with the pre-tag value.
        await qc.cancelQueries({ queryKey: key });
        const previous = qc.getQueriesData<AdsCampaignsView>({ queryKey: key });
        qc.setQueriesData<AdsCampaignsView>({ queryKey: key }, (old) =>
          old?.campaigns
            ? { ...old, campaigns: old.campaigns.map((c) => (c.id === id ? { ...c, releaseId } : c)) }
            : old,
        );
        return { previous };
      },
      onError: (err, _vars, ctx) => {
        for (const [k, data] of ctx?.previous ?? []) qc.setQueryData(k, data);
        pushToast(errorMessage(err, "Couldn't tag the campaign"));
      },
    },
  );
}
